import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  voidAbsenceRequest,
  reverseDecision,
  resolveDocumentation,
  editAbsenceDay,
  updateRequestNotes,
  type ActionResult,
} from '@/lib/actions';
import { TechnicalDetails } from '@/lib/TechnicalDetails';
import { useIsNarrow } from '@/lib/useMediaQuery';
import { color, font, radius, shadow } from '@/lib/theme';
import {
  notCountedLabel,
  type AbsenceFactRow,
  type EmployeeRow,
  type LocationRow,
} from '@/features/dashboard/data';
import { loadManageableRecords } from './data';

/**
 * Manage Records — P5's edit/void surface (BUILD_PLAN §4a's D3, DD1-DD6).
 * A dedicated view, deliberately NOT inline on the Dashboard (D3's own
 * reasoning: that would turn a read-only page into a write surface and widen
 * the blast radius) — its own sidebar destination, same as Log an Absence.
 *
 * Only Approved / Denied / Recorded requests are editable here (BUILD_PLAN's
 * own scope for this page); Pending stays the Approval Queue's business and
 * Needs Documentation has no defined transition here. Dates, absence type,
 * and the employee are never editable — void the record and log a new one
 * if those are wrong (DD6).
 *
 * P8 follow-up F1 (2026-09-10): Voided records are listed too, READ-ONLY,
 * behind a "Show voided (N)" toggle that appears only when the current
 * filters match at least one. Cutover night's question was "can we remove
 * the P5 test records?" — and the answer is that nothing here deletes (DD2,
 * the no-DELETE grants, the append-only outbox), so the honest UI is one that
 * shows a voided record as what it is: kept, struck through, with its void
 * reason, and with no buttons. A real former employee's voided history will
 * look exactly the same. The default view still hides them, so the page's
 * everyday shape is unchanged.
 *
 * F7 (migration 0026, 2026-09-11): Needs Documentation records are listed as
 * LIVE cards with two resolution buttons — "Doctor's note received" (sets the
 * note, recomputes the days' points, → Recorded) and "No note — record
 * as-is" (→ Recorded, points stand) — plus Void and Notes. Their days render
 * read-only until resolved: one state machine, not day edits racing a status
 * change. Until 0026 these records were stuck: this page could not see them
 * and nothing could move them (the old system had the same gap; a manager
 * edited the Sheet's Status cell by hand).
 *
 * `employees` arrives already loaded from the Dashboard's own fetch (same
 * pattern as LogAbsence) — no extra round trip for the roster, and already
 * RLS-scoped to this manager's location (or every location, for an admin).
 * The absence-day facts are this page's OWN fetch (data.ts's
 * `loadManageableRecords()`), unbounded — see that file's header for why
 * reusing the Dashboard's capped `recentFacts` was wrong for a write surface.
 */
export function ManageRecords({
  employees,
  locations,
  onChanged,
}: {
  employees: EmployeeRow[];
  /** P6: for the location filter's dropdown labels only — RLS, not this list,
      is what actually scopes which employees/records a manager can reach. */
  locations: LocationRow[];
  /** Called after any successful write so the Dashboard's own data refreshes quietly too. */
  onChanged: () => void;
}) {
  const narrow = useIsNarrow();
  const [facts, setFacts] = useState<AbsenceFactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    loadManageableRecords().then(setFacts, (e: unknown) => setError(String(e)));
  }, []);
  useEffect(load, [load]);

  // Re-fetches this page's own (unbounded) data AND tells the Dashboard to
  // refresh its summary/calendar so a void/reverse/edit shows up everywhere.
  const refresh = useCallback(() => {
    load();
    onChanged();
  }, [load, onChanged]);

  const nameById = useMemo(() => new Map(employees.map((e) => [e.id, e.full_name])), [employees]);
  const locationById = useMemo(() => new Map(employees.map((e) => [e.id, e.location_id])), [employees]);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // F1: Voided groups are fetched but hidden by default — same "Show inactive"
  // pill the Admin Console's Roster and Locations tabs already use.
  const [showVoided, setShowVoided] = useState(false);

  // P6: same reasoning as the Dashboard's own filter — derive the options
  // from the locations actually present among `employees`, which RLS has
  // already scoped to this manager. Collapses to one entry (control hidden)
  // for a regular manager; lists every staffed location for an admin.
  const employeeLocationIds = useMemo(() => new Set(employees.map((e) => e.location_id)), [employees]);
  const filterableLocations = useMemo(
    () => locations.filter((l) => employeeLocationIds.has(l.id)),
    [locations, employeeLocationIds],
  );
  // Adversarial-review fix: a write here (void/reverse/edit) or an Admin
  // Console edit refreshing this same session's `employees` (see Dashboard's
  // own onChanged wiring) can shrink `filterableLocations` out from under a
  // selected value — its last employee moved or was deactivated. Derive a
  // sanitized value every render, same fix as the Dashboard's own filter,
  // rather than let a now-invalid selection silently narrow everything to
  // zero rows with the control itself already gone (filterableLocations.length
  // dropping to 1 hides the <select> in the same move).
  const activeLocationFilter = filterableLocations.some((l) => l.id === locationFilter)
    ? locationFilter
    : '';

  const employeesAtLocation = activeLocationFilter
    ? employees.filter((e) => e.location_id === activeLocationFilter)
    : employees;

  function onLocationFilterChange(id: string) {
    setLocationFilter(id);
    // A location change can orphan the employee filter (picked someone who
    // isn't at the newly-selected location) — reset rather than leave a
    // silently-invalid combination that always renders "No matching records".
    if (id && employeeFilter && locationById.get(employeeFilter) !== id) {
      setEmployeeFilter('');
    }
  }

  const groups = useMemo(() => buildGroups(facts ?? [], nameById), [facts, nameById]);

  const hasActiveFilter = Boolean(employeeFilter || activeLocationFilter || fromDate || toDate);

  // Every filter EXCEPT the voided toggle — so the toggle's count can say how
  // many voided records the current employee/location/date selection hides.
  const matching = groups.filter((g) => {
    if (employeeFilter && g.employeeId !== employeeFilter) return false;
    // A former (deactivated) employee has no entry in `employees`, so their
    // location is unknowable — hide them under a specific location filter
    // (same "unattributable, so excluded" call the employee filter already
    // makes implicitly) but keep them visible under "All locations".
    if (activeLocationFilter && locationById.get(g.employeeId) !== activeLocationFilter) return false;
    const first = g.days[0]?.absence_date ?? '';
    const last = g.days[g.days.length - 1]?.absence_date ?? '';
    if (fromDate && last < fromDate) return false;
    if (toDate && first > toDate) return false;
    return true;
  });
  const voidedMatching = matching.filter((g) => g.status === 'Voided').length;
  const filtered = showVoided ? matching : matching.filter((g) => g.status !== 'Voided');

  return (
    <div style={S.wrap}>
      {/* Accessibility-review fix (redesign Pass 2): see LogAbsence.tsx's
          matching comment — this page's own title is now the document's h1,
          same pattern the Dashboard view already uses. */}
      <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>Manage Records</h1>
      <p style={S.sub}>
        Correct or void an Approved, Denied, or Recorded record. Dates, absence type, and the
        employee stay locked here — void the record and log a new one if those are wrong. Voiding
        a previously approved request, or reversing Approved ⇄ Denied, emails the employee; every
        other edit here (PTO days, the excused flag, notes) is silent. A record waiting on a
        doctor&rsquo;s note shows here too — mark it Recorded once the note arrives, or as-is if it
        never will. Voided records are kept, never deleted — show them read-only with the toggle
        that appears when your filters match one.
      </p>

      <div style={{ ...S.filters, ...(narrow ? S.filtersNarrow : null) }}>
        {/* P6: hidden for a regular manager — RLS already scopes `employees`
            to one location, so `filterableLocations` collapses to one entry. */}
        {filterableLocations.length > 1 && (
          <select
            value={activeLocationFilter}
            onChange={(e) => onLocationFilterChange(e.target.value)}
            style={S.filterInput}
            aria-label="Filter by location"
          >
            <option value="">All locations</option>
            {filterableLocations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          style={S.filterInput}
          aria-label="Filter by employee"
        >
          <option value="">All employees</option>
          {employeesAtLocation.map((e) => (
            <option key={e.id} value={e.id}>
              {e.full_name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={S.filterInput}
          aria-label="From date"
        />
        <span style={S.filterTo}>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={S.filterInput}
          aria-label="To date"
        />
      </div>

      {/* F1: only offered when the current selection actually hides something,
          so a manager never sees a toggle with nothing behind it. */}
      {!error && facts !== null && hasActiveFilter && voidedMatching > 0 && (
        <label style={S.showVoidedLabel}>
          <input
            type="checkbox"
            checked={showVoided}
            onChange={(e) => setShowVoided(e.target.checked)}
          />
          Show voided ({voidedMatching})
        </label>
      )}

      {error && <p style={S.subError}>Could not load records: {error}</p>}
      {!error && facts === null && <p style={S.subMuted}>Loading…</p>}
      {/* Pre-P8 punch list, Tier 1: the underlying fetch stays unbounded on
          purpose (old records must never silently disappear past a row
          cap) — this only changes whether a card renders with no filter
          set, so a manager isn't handed every decided record in company
          history by default. */}
      {!error && facts !== null && !hasActiveFilter && (
        <p style={S.subMuted}>Select an employee, location, or date range to see records.</p>
      )}
      {!error && facts !== null && hasActiveFilter && filtered.length === 0 && (
        <p style={S.subMuted}>
          {voidedMatching > 0
            ? `No live records match — ${voidedMatching} voided ${voidedMatching === 1 ? 'record is' : 'records are'} hidden.`
            : 'No matching records.'}
        </p>
      )}
      {!error &&
        facts !== null &&
        hasActiveFilter &&
        filtered.map((g) => <RecordCard key={g.requestId} group={g} onChanged={refresh} />)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouping — one card per REQUEST, days rolled up underneath it.
// ---------------------------------------------------------------------------

interface RequestGroup {
  requestId: string;
  employeeId: string;
  employeeName: string;
  absenceType: string;
  status: string;
  notes: string | null;
  voidReason: string | null;
  days: AbsenceFactRow[]; // sorted ascending by date
}

// The three editable statuses, Voided (read-only, F1) and Needs Documentation
// (resolve-only, F7). The fetch already
// filters to exactly these; this is the belt to that brace, so a future
// status added to the enum never renders here by accident.
const LISTED_STATUSES = new Set(['Approved', 'Denied', 'Recorded', 'Voided', 'Needs Documentation']);

function buildGroups(facts: AbsenceFactRow[], nameById: Map<string, string>): RequestGroup[] {
  const byRequest = new Map<string, AbsenceFactRow[]>();
  for (const f of facts) {
    if (!LISTED_STATUSES.has(f.status)) continue;
    const list = byRequest.get(f.request_id);
    if (list) list.push(f);
    else byRequest.set(f.request_id, [f]);
  }

  const groups: RequestGroup[] = [];
  for (const [requestId, days] of byRequest) {
    days.sort((a, b) => a.absence_date.localeCompare(b.absence_date));
    const first = days[0];
    if (first === undefined) continue;
    groups.push({
      requestId,
      employeeId: first.employee_id,
      employeeName: nameById.get(first.employee_id) ?? '(former employee)',
      absenceType: first.absence_type,
      status: first.status,
      notes: first.notes,
      voidReason: first.void_reason,
      days,
    });
  }

  // Newest first, by each group's last day.
  groups.sort((a, b) => {
    const aLast = a.days[a.days.length - 1]?.absence_date ?? '';
    const bLast = b.days[b.days.length - 1]?.absence_date ?? '';
    return bLast.localeCompare(aLast);
  });
  return groups;
}

// ---------------------------------------------------------------------------
// One record card: header, days, notes, actions.
// ---------------------------------------------------------------------------

function RecordCard({ group, onChanged }: { group: RequestGroup; onChanged: () => void }) {
  const narrow = useIsNarrow();
  const [notes, setNotes] = useState(group.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ActionResult | null>(null);

  const first = group.days[0];
  const last = group.days[group.days.length - 1];
  const canReverse = group.status === 'Approved' || group.status === 'Denied';
  // F1: a Voided record is display-only. No DayEditor (its Save would call
  // edit_absence_day, which refuses a Voided day anyway), no notes box, no
  // actions — nothing on a dead record should look pressable.
  const voided = group.status === 'Voided';
  // F7: a Needs Documentation record is live but waiting. Days read-only
  // (edit_absence_day refuses this status by design), resolution buttons on.
  const needsDoc = group.status === 'Needs Documentation';

  async function doResolve(noteReceived: boolean) {
    const question = noteReceived
      ? `Mark ${group.employeeName}'s record as Recorded with the doctor's note on file? Points for these days will be recomputed with the note.`
      : `Record ${group.employeeName}'s absence as-is, with no doctor's note? The points stand and the record stops waiting.`;
    if (!window.confirm(question)) return;
    setBusy(true);
    setResult(null);
    const res = await resolveDocumentation(group.requestId, noteReceived);
    setBusy(false);
    setResult(res);
    if (res.ok) onChanged();
  }

  async function saveNotes() {
    setBusy(true);
    setResult(null);
    const res = await updateRequestNotes(group.requestId, notes);
    setBusy(false);
    setResult(res);
    if (res.ok) onChanged();
  }

  async function doReverse() {
    const target = group.status === 'Approved' ? 'Denied' : 'Approved';
    if (
      !window.confirm(
        `Reverse this request to ${target}? ${group.employeeName} will be emailed about the correction.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setResult(null);
    const res = await reverseDecision(group.requestId);
    setBusy(false);
    setResult(res);
    if (res.ok) onChanged();
  }

  async function doVoid() {
    // Pre-P8 punch list, Tier 1 HIGH finding: voiding is permanent (no
    // un-void path exists anywhere), yet this prompt used to carry none of
    // the identifying context doReverse's confirm dialog already does —
    // mirror that pattern here too.
    const dateRange =
      last && first && last.absence_date !== first.absence_date
        ? `${first.absence_date} → ${last.absence_date}`
        : (first?.absence_date ?? '');
    const reason = window.prompt(
      `Permanently void ${group.employeeName}'s ${dateRange} record? This cannot be undone. Reason for voiding (required):`,
    );
    if (reason === null) return; // cancelled
    if (reason.trim() === '') {
      setResult({ ok: false, error: 'A reason is required to void a record.' });
      return;
    }
    setBusy(true);
    setResult(null);
    const res = await voidAbsenceRequest(group.requestId, reason);
    setBusy(false);
    setResult(res);
    if (res.ok) onChanged();
  }

  if (voided) {
    return (
      <div style={{ ...S.card, ...S.cardVoided, ...(narrow ? S.cardNarrow : null) }}>
        <div style={S.cardHeader}>
          <div style={S.cardHeaderLeft}>
            <span style={{ ...S.employeeName, ...S.struck }}>{group.employeeName}</span>
            <span style={S.typePill}>{group.absenceType}</span>
            <span style={{ ...S.statusPill, ...S.statusPillVoided }}>Voided</span>
          </div>
          <div style={{ ...S.dateRange, ...S.struck }}>
            {first?.absence_date}
            {last && first && last.absence_date !== first.absence_date ? ` → ${last.absence_date}` : ''}
          </div>
        </div>
        <ul style={S.voidedDays} aria-label="Days on this voided record">
          {group.days.map((d) => (
            <li key={d.id} style={S.voidedDay}>
              <span style={S.dayDate}>{d.absence_date}</span>
              {d.absence_type === 'PTO' && (
                <span>
                  {d.pto_days} PTO {d.pto_days === 0 ? 'days' : 'day'}
                </span>
              )}
              {d.not_counted_reason !== null && (
                <span style={S.dayProtected}>{notCountedLabel(d.not_counted_reason, d.not_counted_note)}</span>
              )}
            </li>
          ))}
        </ul>
        <p style={S.voidReason}>
          <strong>Voided:</strong> {group.voidReason ?? 'no reason recorded'}
        </p>
        {group.notes && (
          <p style={S.voidedNotes}>
            <strong>Notes:</strong> {group.notes}
          </p>
        )}
        <p style={S.subMuted}>
          Kept for the record — a voided entry counts toward nothing and cannot be edited or
          restored. If the absence was real, log it again.
        </p>
      </div>
    );
  }

  if (needsDoc) {
    return (
      <div style={{ ...S.card, ...(narrow ? S.cardNarrow : null), ...S.cardNeedsDoc }}>
        <div style={S.cardHeader}>
          <div style={S.cardHeaderLeft}>
            <span style={S.employeeName}>{group.employeeName}</span>
            <span style={S.typePill}>{group.absenceType}</span>
            <span style={{ ...S.statusPill, ...S.statusPillNeedsDoc }}>Needs Documentation</span>
          </div>
          <div style={S.dateRange}>
            {first?.absence_date}
            {last && first && last.absence_date !== first.absence_date ? ` → ${last.absence_date}` : ''}
          </div>
        </div>
        <ul style={S.voidedDays} aria-label="Days on this record">
          {group.days.map((d) => (
            <li key={d.id} style={S.pendingDay}>
              <span style={S.dayDate}>{d.absence_date}</span>
              <span>{d.points} pts</span>
              {d.not_counted_reason !== null && (
                <span style={S.dayProtected}>{notCountedLabel(d.not_counted_reason, d.not_counted_note)}</span>
              )}
            </li>
          ))}
        </ul>
        <p style={S.needsDocText}>
          This absence spans 2+ days with no doctor&rsquo;s note on file. The days and their points
          count in the meantime. When you know the outcome:
        </p>
        <div style={S.actionsRow}>
          <button onClick={() => void doResolve(true)} disabled={busy} style={S.saveBtn}>
            Doctor&rsquo;s note received
          </button>
          <button onClick={() => void doResolve(false)} disabled={busy} style={S.reverseBtn}>
            No note &mdash; record as-is
          </button>
          <button onClick={() => void doVoid()} disabled={busy} style={S.voidBtn}>
            Void
          </button>
        </div>

        <label style={S.label} htmlFor={`notes-${group.requestId}`}>
          Notes
        </label>
        <textarea
          id={`notes-${group.requestId}`}
          rows={2}
          placeholder="e.g. asked for the note on 9/12"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ ...S.input, resize: 'vertical' }}
        />
        <div style={S.actionsRow}>
          <button onClick={() => void saveNotes()} disabled={busy} style={S.saveBtn}>
            Save Notes
          </button>
        </div>

        <p role="status" aria-live="polite" style={result?.ok ? S.resultOk : S.resultEmpty}>
          {result?.ok ? (result.message ?? 'Done.') : ''}
        </p>
        {result && !result.ok && (
          <div style={S.resultErr}>
            <p role="alert" style={S.resultText}>
              {result.error ?? 'Something went wrong.'}
            </p>
            <TechnicalDetails detail={result.detail} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...S.card, ...(narrow ? S.cardNarrow : null) }}>
      <div style={S.cardHeader}>
        <div style={S.cardHeaderLeft}>
          <span style={S.employeeName}>{group.employeeName}</span>
          <span style={S.typePill}>{group.absenceType}</span>
          <span style={S.statusPill}>{group.status}</span>
        </div>
        <div style={S.dateRange}>
          {first?.absence_date}
          {last && first && last.absence_date !== first.absence_date ? ` → ${last.absence_date}` : ''}
        </div>
      </div>

      <div style={S.daysList}>
        {group.days.map((d) => (
          // Keyed on the row id ALONE (it used to carry the saved values too,
          // which remounted the row on every save — and with it the Save
          // button holding keyboard focus, dropping focus to <body>; review
          // finding). DayEditor re-seeds its fields itself when the saved
          // values change underneath it.
          <DayEditor
            key={d.id}
            day={d}
            onChanged={onChanged}
            // The confirmation lands on the card's result banner — already
            // where Save Notes / Reverse / Void report.
            onSaved={(message) => setResult({ ok: true, message })}
          />
        ))}
      </div>

      <label style={S.label} htmlFor={`notes-${group.requestId}`}>
        Notes
      </label>
      <textarea
        id={`notes-${group.requestId}`}
        rows={2}
        placeholder="Optional"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        style={{ ...S.input, resize: 'vertical' }}
      />

      <div style={S.actionsRow}>
        <button onClick={() => void saveNotes()} disabled={busy} style={S.saveBtn}>
          Save Notes
        </button>
        {canReverse && (
          <button onClick={() => void doReverse()} disabled={busy} style={S.reverseBtn}>
            Reverse to {group.status === 'Approved' ? 'Denied' : 'Approved'}
          </button>
        )}
        <button onClick={() => void doVoid()} disabled={busy} style={S.voidBtn}>
          Void
        </button>
      </div>

      {/* The success line is an ALWAYS-mounted polite live region (an empty
          one that gains text is announced; one created already holding text
          is not — review finding). Errors stay a conditional role="alert"
          block with the disclosure as its sibling, the app's usual split. */}
      <p role="status" aria-live="polite" style={result?.ok ? S.resultOk : S.resultEmpty}>
        {result?.ok ? (result.message ?? 'Done.') : ''}
      </p>
      {result && !result.ok && (
        <div style={S.resultErr}>
          <p role="alert" style={S.resultText}>
            {result.error ?? 'Something went wrong.'}
          </p>
          <TechnicalDetails detail={result.detail} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One day's PTO days + excused flag/reason — DD6's day-level fields, silent.
// ---------------------------------------------------------------------------

/**
 * Pre-P8 punch list, Tier 2 (mobile): this row used to be one wrapping line
 * of 0.82rem controls — a 29px select, a checkbox with a tap target the size
 * of its own box, a text input and a small Save, all wrapping unpredictably
 * on a phone. Now:
 *   - Below 768px (`useIsNarrow`) the controls stack: date and PTO days on
 *     the first line, the excused checkbox as its own full-width 44px row,
 *     the reason box under it, Save last and full width. Every control is
 *     ≥44px tall. On desktop the single-line layout stays, at 16px.
 *   - Save is disabled until something actually differs from the saved row,
 *     so N identical Save buttons no longer invite N no-op RPC calls, and it
 *     names its day for screen readers (a card with five days has five Saves).
 *   - "Excused" with a blank reason is refused HERE with a plain sentence
 *     before it reaches the RPC (which refuses it too, less kindly).
 *   - Success is reported on the card's banner (see the caller) — the row
 *     itself remounts with the saved values.
 *
 * Deliberately NOT built: a per-request "Save all days". Each day is its own
 * RPC, its own audit row and its own possible refusal (a protected reason, a
 * stale row), so a batch needs partial-failure reporting per day — which is
 * exactly what the per-day Save already is. The disabled-until-dirty Save
 * makes "which days did I touch" visible instead, which is the confusion the
 * punch-list item actually described. Revisit if multi-day edits become
 * routine; today's data has one multi-day group with day-level differences.
 */
function DayEditor({
  day,
  onChanged,
  onSaved,
}: {
  day: AbsenceFactRow;
  onChanged: () => void;
  onSaved: (message: string) => void;
}) {
  const narrow = useIsNarrow();
  // 🔴 Adversarial-review fix: edit_absence_day's excused flag is DD3/DD6's
  // ONE manager-settable exclusion — never weekend/company_holiday/not_pto/
  // manager_excluded (the v13 toggle; Katie Siegfried's one real historical
  // row). The RPC itself now refuses to clear anything but 'excused', but the
  // UI must match that intent too: a day excluded for one of those OTHER
  // reasons gets a plain read-only label, never an unchecked checkbox that
  // implies "not excused, click to exclude" when it already is, for a reason
  // this form has no business touching.
  const isProtectedReason =
    day.not_counted_reason !== null && day.not_counted_reason !== 'excused';

  const [ptoDays, setPtoDays] = useState(day.pto_days);
  const [excused, setExcused] = useState(day.not_counted_reason === 'excused');
  const [reason, setReason] = useState(day.not_counted_note ?? '');
  const [busy, setBusy] = useState(false);
  // True from a successful save until the refreshed row arrives: `day` is
  // still the pre-save row in that window, so `dirty` would stay true and
  // Save would re-enable for a second, identical RPC (review finding).
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<{ text: string; detail?: string } | null>(null);
  const errorId = useId();

  const savedExcused = day.not_counted_reason === 'excused';
  const savedReason = day.not_counted_note ?? '';
  // Re-seed from the row whenever its saved values change underneath (our
  // own save landing, or another manager's) — the component is keyed on the
  // row id, so this is what a remount used to do, minus the lost focus.
  useEffect(() => {
    setPtoDays(day.pto_days);
    setExcused(savedExcused);
    setReason(savedReason);
    setSaved(false);
    setError(null);
  }, [day.pto_days, savedExcused, savedReason]);

  const dirty =
    (day.absence_type === 'PTO' && ptoDays !== day.pto_days) ||
    (!isProtectedReason && (excused !== savedExcused || (excused && reason.trim() !== savedReason)));

  async function save() {
    // Click guard, not `disabled`: this button holds focus while it flips to
    // busy and then to saved, and a focused button that becomes `disabled`
    // is blurred by Chrome — focus fell to <body> on every save, caught live
    // (the calendar's retry button hit the identical thing in the overnight
    // run). aria-disabled + this guard keep focus where the manager left it.
    if (saveDisabled) return;
    if (!isProtectedReason && excused && reason.trim() === '') {
      setError({ text: 'Give a reason before marking this day excused.' });
      return;
    }
    setBusy(true);
    setError(null);
    const res = await editAbsenceDay({
      dayId: day.id,
      ptoDays: day.absence_type === 'PTO' ? ptoDays : null,
      // null = "leave the exclusion alone" (editAbsenceDay's own contract) —
      // sent for a protected reason so this form can never touch it, even if
      // a future RPC change loosens the server-side guard.
      excused: isProtectedReason ? null : excused,
      excusedReason: isProtectedReason ? null : excused ? reason.trim() : null,
    });
    setBusy(false);
    if (res.ok) {
      setSaved(true);
      onSaved(`Saved ${day.absence_date}.`);
      onChanged();
    } else {
      setError({ text: res.error ?? 'Something went wrong.', detail: res.detail });
    }
  }

  const saveDisabled = busy || saved || !dirty;
  const control = narrow ? S.dayControlNarrow : null;
  const reasonMissing = error !== null && excused && reason.trim() === '';

  const dateAndAmount = (
    <>
      <span style={S.dayDate}>{day.absence_date}</span>

      {day.absence_type === 'PTO' && (
        <select
          value={String(ptoDays)}
          onChange={(e) => setPtoDays(Number(e.target.value))}
          style={{ ...S.daySelect, ...control, ...(narrow ? S.dayGrow : null) }}
          aria-label="PTO days"
        >
          <option value="0">0 PTO days</option>
          <option value="0.5">0.5 PTO day</option>
          <option value="1">1 PTO day</option>
        </select>
      )}

      {isProtectedReason && (
        <span style={S.dayProtected}>
          {notCountedLabel(day.not_counted_reason!, day.not_counted_note)} — not editable here
        </span>
      )}
    </>
  );

  return (
    // role="group" named by the date: every control inside — the checkbox,
    // whose visible text is identical on every day of a multi-day card, the
    // select, the reason, Save — is read with the date as context, so none
    // needs the date baked into its own label (review finding).
    <div role="group" aria-label={day.absence_date} style={narrow ? S.dayRowNarrow : S.dayRow}>
      {narrow ? <div style={S.dayLineNarrow}>{dateAndAmount}</div> : dateAndAmount}

      {!isProtectedReason && (
        <label style={{ ...S.dayCheckbox, ...(narrow ? S.dayCheckboxNarrow : null) }}>
          <input
            type="checkbox"
            checked={excused}
            onChange={(e) => {
              setExcused(e.target.checked);
              setError(null);
            }}
            style={narrow ? S.dayCheckboxBoxNarrow : undefined}
          />
          Excused (doesn&rsquo;t count)
        </label>
      )}

      {!isProtectedReason && excused && (
        <input
          type="text"
          placeholder="Reason (required)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonMissing) setError(null);
          }}
          aria-label="Reason this day is excused"
          aria-required
          aria-invalid={reasonMissing ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          style={{ ...S.dayReasonInput, ...control }}
        />
      )}

      <button
        onClick={() => void save()}
        aria-disabled={saveDisabled}
        aria-busy={busy}
        style={{
          ...S.daySaveBtn,
          ...control,
          ...(narrow ? S.dayGrow : null),
          ...(saveDisabled ? S.daySaveBtnDisabled : null),
        }}
      >
        {busy ? 'Saving…' : 'Save'}
      </button>

      {error && (
        <div style={S.dayError}>
          <span id={errorId} role="alert">
            {error.text}
          </span>
          <TechnicalDetails detail={error.detail} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles — Direction D redesign pass (2026-09-04): tokens from
// `src/lib/theme.ts`, matching the conventions already shipped in
// Dashboard.tsx / AppShell.tsx. Visual values only — see DASHBOARD_REDESIGN.md
// follow-up item 6. Error red (#b00020 / #fdecef) is a deliberate literal
// carry-over: it's an established shipped Direction D convention, not yet a
// named theme.ts token (see that file's own header note for why).
// ---------------------------------------------------------------------------

const S = {
  wrap: { maxWidth: '48rem', margin: '0 auto' },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    fontFamily: font.display,
    color: color.ink,
  },
  titleNarrow: { fontSize: '20px' },
  sub: { color: color.textMuted, fontSize: '14px', lineHeight: 1.5, marginTop: '4px', marginBottom: '1.25rem' },
  subMuted: { color: color.textMuted, fontSize: '0.9rem' },
  subError: { color: '#b00020', fontSize: '0.9rem' },
  // Called out explicitly as a card container (pattern 3) in the redesign
  // spec, not just a plain flex row — the filter controls now sit inside
  // the same bordered/padded/shadowed box every other content block uses.
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    marginBottom: '1.25rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.cardDesktop,
    padding: '18px 20px',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  filtersNarrow: { borderRadius: radius.cardMobile, padding: '14px 16px', boxShadow: 'none' },
  // 1rem = 16px on every control here: stops iOS Safari's focus auto-zoom
  // (RequestTimeOff.tsx's S.input has the story).
  filterInput: {
    fontFamily: font.body,
    fontSize: '1rem',
    padding: '0.5rem 0.7rem',
    minHeight: '2.75rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '0.6rem',
    background: color.cardBg,
    color: color.ink,
    boxSizing: 'border-box',
  },
  filterTo: { fontSize: '0.85rem', color: color.textMuted },
  card: {
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.cardDesktop,
    padding: '26px 28px 22px',
    marginBottom: '1rem',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  cardNarrow: { borderRadius: radius.cardMobile, padding: '16px', boxShadow: 'none' },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  cardHeaderLeft: { display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' },
  employeeName: { fontWeight: 700, fontSize: '1rem', fontFamily: font.display, color: color.ink },
  // Chip/badge pattern (11): outlined neutral variant for the type label,
  // filled neutral variant for status — same distinction the old grays drew,
  // now on theme tokens.
  typePill: {
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: radius.pill,
    border: `1px solid ${color.cardBorder}`,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: color.textSubtle,
    whiteSpace: 'nowrap',
  },
  // Cross-file consistency review: matches Dashboard.tsx's own S.statusPill
  // exactly (outlined, not filled) — same status-label role, was drifting
  // into its own filled/larger treatment.
  statusPill: {
    display: 'inline-block',
    padding: '0.05rem 0.45rem',
    borderRadius: radius.pill,
    border: `1px solid ${color.cardBorder}`,
    fontSize: '0.72rem',
    color: color.textSubtle,
    whiteSpace: 'nowrap',
  },
  dateRange: { fontSize: '0.85rem', color: color.textMuted, fontVariantNumeric: 'tabular-nums' },
  daysList: { marginBottom: '0.75rem' },
  // F1 — the read-only Voided card. Same Direction D pill toggle as the Admin
  // Console's "Show inactive (N)" (pattern 7), and the Dashboard activity
  // log's own strike-through treatment for a voided line (S.logVoided there).
  showVoidedLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2.75rem',
    gap: '6px',
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: font.body,
    color: color.textSubtle,
    background: color.pillNeutralBg,
    borderRadius: radius.pill,
    padding: '0 16px',
    marginBottom: '12px',
    cursor: 'pointer',
  },
  cardVoided: { background: color.pageBg, boxShadow: 'none' },
  // F7 — the waiting card: amber accent (the app's "attention" hue, same as
  // dayProtected) on the left edge and the status pill.
  // Inset shadow rather than borderLeft: S.card sets the `border` SHORTHAND,
  // and layering a longhand over it is the React dev warning the redesign's
  // SectionCard already hit once (DASHBOARD_REDESIGN.md). Same reason the
  // pill restates the whole `border` instead of borderColor.
  cardNeedsDoc: { boxShadow: `${shadow.card}, inset 4px 0 0 ${color.amberDark}` },
  statusPillNeedsDoc: { border: `1px solid ${color.amberDark}`, color: color.amberDark },
  pendingDay: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    padding: '0.45rem 0',
    borderBottom: `1px solid ${color.pageBg}`,
    fontSize: '0.85rem',
    color: color.ink,
  },
  needsDocText: { fontSize: '0.9rem', color: color.textMuted, margin: '0 0 0.6rem' },
  struck: { textDecoration: 'line-through', color: color.textFaint },
  statusPillVoided: { borderStyle: 'dashed' },
  voidedDays: { listStyle: 'none', margin: '0 0 0.75rem', padding: 0 },
  voidedDay: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    padding: '0.45rem 0',
    borderBottom: `1px solid ${color.cardBorder}`,
    fontSize: '0.85rem',
    color: color.textFaint,
  },
  voidReason: { fontSize: '0.9rem', color: color.ink, margin: '0 0 0.35rem' },
  voidedNotes: { fontSize: '0.9rem', color: color.textMuted, margin: '0 0 0.35rem' },
  // Desktop: one wrapping line, as before but at 16px. Phone (`dayRowNarrow`
  // and the *Narrow keys): stacked, every control ≥44px — see DayEditor.
  dayRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    padding: '0.45rem 0',
    borderBottom: `1px solid ${color.pageBg}`,
    fontSize: '0.85rem',
  },
  dayRowNarrow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '0.5rem',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${color.pageBg}`,
    fontSize: '0.95rem',
  },
  // Phone only: date + select share one line inside this wrapper. On desktop
  // the same children render straight into the flex row (no wrapper).
  dayLineNarrow: { display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' },
  dayGrow: { flex: '1 1 auto' },
  // `flex: 0 0 auto` matters: the row is a COLUMN on a phone, so a control's
  // row-oriented flex-basis (the reason box's 12rem) would become its HEIGHT
  // — it rendered 192px tall, caught live.
  dayControlNarrow: { flex: '0 0 auto', minHeight: '2.75rem', fontSize: '1rem' },
  dayDate: { minWidth: '6rem', color: color.ink, fontVariantNumeric: 'tabular-nums' },
  daySelect: {
    fontFamily: font.body,
    padding: '0.5rem 0.7rem',
    fontSize: '1rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '0.6rem',
    background: color.cardBg,
    color: color.ink,
  },
  dayCheckbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
    color: color.ink,
  },
  // The whole label is the tap target: 44px tall, and the box itself scaled
  // up so it does not read as a 13px dot on a 44px row.
  dayCheckboxNarrow: { minHeight: '2.75rem', gap: '0.6rem' },
  dayCheckboxBoxNarrow: { width: '1.35rem', height: '1.35rem', margin: 0 },
  dayProtected: { fontStyle: 'italic', color: color.amberDark, fontSize: '0.82rem' },
  dayReasonInput: {
    fontFamily: font.body,
    flex: '1 1 12rem',
    minWidth: '10rem',
    padding: '0.5rem 0.7rem',
    fontSize: '1rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '0.6rem',
    background: color.cardBg,
    color: color.ink,
    boxSizing: 'border-box',
  },
  // Small pill utility button (pattern 7 shape, sized for an inline per-day
  // row) reusing pattern 5's already-verified white-on-brandBlue pair rather
  // than inventing a new color combination.
  daySaveBtn: {
    padding: '0.35rem 0.85rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  // No stylesheet means no :disabled rule — spell out "nothing to save" so a
  // greyed Save is visibly different from a live one (RequestTimeOff's Submit
  // learned the same lesson).
  // Full `border` shorthand, not `borderColor`: React warns (and can mis-style)
  // when a shorthand and its longhand are mixed across a rerender — caught in
  // the console during the click-through.
  daySaveBtnDisabled: {
    // Cross-file consistency review: textFaint-on-pillNeutralBg measured
    // ~4.18:1, a real (if WCAG-exempt for a disabled control) regression
    // from this file's pre-redesign value. textSubtle clears AA (~5.37:1
    // on this background) and is still visibly muted next to the enabled
    // button.
    color: color.textSubtle,
    background: color.pillNeutralBg,
    border: `1px solid ${color.cardBorder}`,
    cursor: 'default',
  },
  dayError: { color: '#b00020', fontSize: '0.82rem' },
  label: {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    marginBottom: '0.35rem',
    color: color.textSubtle,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: font.body,
    padding: '0.5rem 0.7rem',
    fontSize: '1rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '0.6rem',
    marginBottom: '0.75rem',
    background: color.cardBg,
    color: color.ink,
  },
  actionsRow: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' },
  // Primary action button (pattern 5).
  saveBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  // Secondary/neutral button (pattern 6) — kept to the verified
  // textSubtle-on-white/cardBorder combo rather than inventing a new
  // amber-toned button pairing outside the patterns given.
  reverseBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.textSubtle,
    background: 'white',
    border: `1.5px solid ${color.cardBorder}`,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  // Destructive action: same shape as the secondary button, error-red text
  // (the established Direction D error convention) and the file's own
  // pre-existing red-tinted border hex, carried over rather than invented.
  voidBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: '#b00020',
    background: 'white',
    border: '1px solid #e5b3bb',
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  resultText: { margin: 0 },
  resultEmpty: { margin: 0 },
  // Cross-file consistency review: aligned to the same success-banner pair
  // LogAbsence.tsx and AdminConsole.tsx already use (color.teal on
  // color.chipTealBg) rather than this file's own leftover green — no
  // theme.ts "success" token exists yet, so this is reuse of an existing
  // token pair for consistency, not a new invention.
  resultOk: {
    margin: '0.75rem 0 0',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.chipTealBg,
    color: color.teal,
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  // Error banner (pattern 9): same shape as the amber warning banner, red-toned.
  resultErr: {
    margin: '0.75rem 0 0',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: '#fdecef',
    color: '#b00020',
    border: '1px solid #e5b3bb',
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
} satisfies Record<string, React.CSSProperties>;
